import { Fragment, useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { Redirect, Link } from 'react-router-dom';
import { Translate, translate } from 'react-i18nify';
import moment from 'moment';
import api from '../../api/apiClient';
import { loginRequest, loginSuccess, loginError, acknowledgeLoginError, showMaintenanceModal } from '../../actions';
import { saveState } from '../../services/localStorage';
import {
  APPLICATION_BASE_PATH,
  API_ERROR_CODES,
  DATE_FORMAT,
  MAINTENANCE_STORAGE_KEY,
  AUTHORITY
} from '../../constants';
import metadata from '../../metadata.json';
import Logo from '../../assets/clinic-logo.svg?react';
import Button from '../Button';
import Spinner from '../Spinner';
import Dialog from '../Journal/Dialog';
import './LoginView.scss';
import Visible from '../Journal/Visible';
import ResolutionBlocker from '../ResolutionBlocker';
import Checkbox from '../Checkbox';
import { useContent } from '../ContentProvider';
import Hint from '../Journal/Hint';
import useBmsVersion from '../BMSLabelling/useBmsVersion';
import UnsupportedBrowserWarning from '../UnsupportedBrowserWarning';

const allowCredentialsLogin =
  process.env.NODE_ENV === 'development' || import.meta.env.VITE_CREDENTIALS_LOGIN === 'enabled';
const sithsLoginEnabled = import.meta.env.VITE_SITHS_LOGIN === 'enabled';

const POLL_RATE_IN_MS = 1000;

const getTimeUntilNextRequest = (requestTime) => {
  return POLL_RATE_IN_MS - requestTime > 0 ? POLL_RATE_IN_MS - requestTime : 0;
};

const LoginView = (props) => {
  const [loginState, _setLoginState] = useState('idle');
  const loginStateRef = useRef('idle');
  const [credentialLoginModalVisible, setCredentialLoginModalVisible] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [activeQrCode, setActiveQrCode] = useState('');
  const [authProvider, setAuthProvider] = useState();
  const [sithsIframeUrl, setSithsIframeUrl] = useState();
  const timeoutRef = useRef();
  const { previewMode, togglePreviewMode } = useContent();
  const bmsVersion = useBmsVersion();

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  useEffect(() => {
    if (props.loginError) {
      setLoginState('error');
    }
  }, [props.loginError]);

  const checkMaintenanceMode = (loginResponse) => {
    if (loginResponse.secondsUntilMaintenanceMode) {
      props.showMaintenanceModal();
      const maintenanceStartsAt = moment().add(loginResponse.secondsUntilMaintenanceMode, 's');
      saveState(maintenanceStartsAt.format(DATE_FORMAT), MAINTENANCE_STORAGE_KEY);
    }
  };

  const beginLoginFlow = async (provider, onSuccess) => {
    setAuthProvider(provider);

    setLoginState('starting');
    try {
      const start = new Date();
      const response = await api.beginLoginFlow(provider);
      const finish = new Date();
      onSuccess(response);
      setLoginState('polling');
      setTimeout(
        () => {
          refreshQrCode(response.body.sessionId, provider);
        },
        getTimeUntilNextRequest(finish - start)
      );
    } catch (err) {
      setLoginState('error');
      props.onLoginError(err);
    }
  };

  const refreshQrCode = async (sessionId, provider) => {
    if (!sessionId) {
      return;
    }

    try {
      const start = new Date();
      const response = await api.refreshQrCode(sessionId, provider);
      const finish = new Date();
      const status = response.body.sessionResponse?.grandidObject?.message?.status;
      const hintCode = response.body.sessionResponse?.grandidObject?.message?.hintCode;

      if (hintCode === 'userSign') {
        setLoginState('user_signing');
      }

      if (status === 'failed') {
        if (hintCode === 'userCancel') {
          setLoginState('idle');
        } else {
          props.onLoginError(translate('login_view.login_failed'));
          setLoginState('error');
        }
        return;
      }

      if (status === 'succeeded' && response.body.loginResponse) {
        setLoginState('authenticated');
        checkMaintenanceMode(response.body.loginResponse);
        props.onLoginSuccess(response.body.loginResponse);
        return;
      }

      if (provider === 'bankid') {
        setActiveQrCode(window.atob(response.body.sessionResponse.grandidObject.QRCode));
      }

      if (['polling', 'user_signing'].includes(loginStateRef.current)) {
        timeoutRef.current = setTimeout(
          () => {
            refreshQrCode(sessionId, provider);
          },
          getTimeUntilNextRequest(finish - start)
        );
      }
    } catch (err) {
      console.log(err);
      setLoginState('error');
      props.onLoginError(err);
      clearTimeout(timeoutRef.current);
    }
  };

  const loginWithCredentials = () => {
    if (!username || !password) {
      return;
    }

    props.onLoginRequest();
    setLoginState('starting');

    setCredentialLoginModalVisible(false);
    const credentials = { identifier: username, password: password, method: 'credentials' };

    api
      .logInTest(credentials)
      .then((result) => {
        checkMaintenanceMode(result);
        props.onLoginSuccess(result);
      })
      .catch((error) => {
        props.onLoginError(error);
      });
  };

  const onUsernameInput = (e) => {
    setUsername(e.target.value);
  };

  const onPasswordInput = (e) => {
    setPassword(e.target.value);
  };

  const toggleCredentialLoginModal = () => setCredentialLoginModalVisible(!credentialLoginModalVisible);

  const cancelLogin = () => {
    setLoginState('idle');
    setActiveQrCode('');
    clearTimeout(timeoutRef.current);
    setSithsIframeUrl(undefined);
  };

  const retry = () => {
    props.acknowledgeLoginError();
    setLoginState('idle');
  };

  const setLoginState = (newState) => {
    _setLoginState(newState);
    loginStateRef.current = newState;
  };

  const from = {
    pathname:
      props.location?.state?.from?.pathname && props.location.state.from.pathname !== `/${APPLICATION_BASE_PATH}`
        ? props.location.state.from.pathname
        : `/${APPLICATION_BASE_PATH}${props.authorities?.includes(AUTHORITY.HANDLE_NEXT_GEN_PATIENTS) ? '/inbox' : ''}`
  };

  if (props.redirectToReferrer && !props.loginError) {
    return <Redirect to={from} />;
  }

  return (
    <Fragment>
      <ResolutionBlocker minWidth={1440} minHeight={900} />
      <UnsupportedBrowserWarning />
      <div className="login-container">
        <div className="login-form">
          <Logo className="clinic-logo" />
          {loginState === 'idle' ? (
            <div className="login-button-wrapper stack-1">
              <Button
                onClick={() => beginLoginFlow('bankid', (res) => setActiveQrCode(window.atob(res.body.QRCode)))}
                className="login-button bank-id"
                fullWidth
              >
                <Translate value="login_view.log_in_with_bank_id" />
              </Button>
              {sithsLoginEnabled ? (
                <Button
                  onClick={() => beginLoginFlow('siths', (res) => setSithsIframeUrl(res.body.redirectUrl))}
                  className="login-button siths"
                  fullWidth
                >
                  <Translate value="login_view.log_in_with_siths" />
                </Button>
              ) : null}
            </div>
          ) : null}

          {loginState === 'starting' ? (
            <div className="flex justify-center mt-10">
              <Spinner />
            </div>
          ) : null}

          <Visible when={authProvider === 'bankid'}>
            {loginState === 'polling' && activeQrCode ? (
              <>
                <div className="flex justify-center" dangerouslySetInnerHTML={{ __html: activeQrCode }} />
                <div className="text-left">
                  <span
                    dangerouslySetInnerHTML={{
                      __html: translate('login_view.instruction1')
                    }}
                  />
                </div>
                <div className="mt-40">
                  <Button variant="text" onClick={cancelLogin}>
                    <Translate value="login_view.cancel_login" />
                  </Button>
                </div>
              </>
            ) : null}

            {loginState === 'user_signing' ? (
              <div>
                <p>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: translate('login_view.instruction2')
                    }}
                  />
                </p>
                <div className="flex justify-center">
                  <Spinner />
                </div>
              </div>
            ) : null}
          </Visible>

          <Visible when={authProvider === 'siths'}>
            {loginState === 'polling' && sithsIframeUrl ? (
              <>
                <iframe src={sithsIframeUrl} className="siths-iframe"></iframe>
                <div className="mt-40">
                  <Button variant="text" onClick={cancelLogin}>
                    <Translate value="login_view.cancel_login" />
                  </Button>
                </div>
              </>
            ) : null}
          </Visible>

          {loginState === 'error' ? (
            <>
              <div className="error-message">
                {props.loginError && props.loginError.response ? (
                  <Translate
                    value={API_ERROR_CODES[JSON.parse(props.loginError.response.text).code] || 'server_error.fallback'}
                  />
                ) : typeof props.loginError === 'string' ? (
                  <span>{props.loginError}</span>
                ) : (
                  <Translate value="server_error.connectivity" />
                )}
              </div>
              <Button onClick={retry} variant="secondary" className="mt-20">
                <Translate value="global.buttons.retry" />
              </Button>
            </>
          ) : null}
        </div>

        <footer className="login-footer">
          <strong>Blodtrycksdoktorn AB</strong>
          <span className="flex mr-5 ml-5">•</span>
          <span>Bredbandet 1, 392 51 Kalmar • 0480-797 960 • support@blodtrycksdoktorn.se</span>
          <div className="build-number">
            {bmsVersion?.data?.version && <Link to="/bms">{bmsVersion.data.version}</Link>}
            <span
              className={allowCredentialsLogin ? 'pointer' : undefined}
              onClick={allowCredentialsLogin ? toggleCredentialLoginModal : undefined}
            >
              {bmsVersion?.data?.version ? ' - ' : ''}
              Clinic: {metadata.version} Build: #{import.meta.env.VITE_BITRISE_BUILD_NUMBER || 0}
            </span>
          </div>
        </footer>
      </div>
      <Dialog
        isOpen={credentialLoginModalVisible}
        onClose={toggleCredentialLoginModal}
        onEnterPress={loginWithCredentials}
        size="small"
        actions={
          <Button onClick={loginWithCredentials} disabled={!username.length || !password.length}>
            <Translate value="login_view.login" />
          </Button>
        }
      >
        <div className="stack-1">
          <div>
            <input
              placeholder="E-postadress"
              type="text"
              className="w-100"
              value={username}
              onChange={onUsernameInput}
            />
          </div>
          <div>
            <input
              placeholder="Lösenord"
              type="password"
              className="w-100"
              value={password}
              onChange={onPasswordInput}
            />
          </div>
          <div className="mt-20 mb-5">
            <h4 className="font-inter fw-500">Contentful-inställningar</h4>
            <div className="flex vertical-align gap-5">
              <Checkbox checked={previewMode} onChange={togglePreviewMode}>
                Använd preview
              </Checkbox>
              <Hint text={'Hämtar opublicerat innehåll med status "Draft".'} />
            </div>
          </div>
        </div>
      </Dialog>
    </Fragment>
  );
};

const mapStateToProps = (state) => {
  return {
    redirectToReferrer: state.auth.redirectToReferrer,
    isLoggingIn: state.auth.isLoggingIn,
    loginError: state.auth.error,
    authorities: state.auth.authorities
  };
};

const mapActionsToProps = {
  onLoginRequest: loginRequest,
  onLoginSuccess: loginSuccess,
  onLoginError: loginError,
  acknowledgeLoginError,
  showMaintenanceModal
};

export default connect(mapStateToProps, mapActionsToProps)(LoginView);
